import React, { memo } from 'react';
import './PracticeArea.css';

interface ParsedKey {
  baseKey: string;
  imageState?: string;
  displayKey: string;
  keyType: 'spell' | 'summoner' | 'auto' | 'item' | 'rightclick' | 'leftclick';
  target?: string;
  description?: string;
  requiresConfirmation?: boolean;
  subSequenceId?: number;
  subSequencePosition?: number;
}

interface KitingSettings {
  enabled: boolean;
  rightClickKey: string;
  hideRCButtons: boolean;
  hideLCButtons: boolean;
  attackMoveClick: boolean;
}

interface ClickFeedback {
  x: number;
  y: number;
  show: boolean;
}

interface UserInputKey {
  key: string;
  isCorrect: boolean;
  isOptional?: boolean;
}

interface ComboData {
  id: string;
  name: string;
  description: string;
  championName: string;
  difficulty: string;
  category: string;
  type: 'simple-combo' | 'jungle-clear';
  inputSequence: string;
  detailedSequence: any[];
}

interface SequenceProgress {
  currentKeyIndex: number;
  currentChunkIndex: number;
  completedKeys: number;
  totalKeys: number;
  errorCount: number;
}

interface KeyState {
  key: ParsedKey;
  status: 'completed' | 'current' | 'upcoming';
  userInput?: UserInputKey;
}

interface AlignedInput {
  expected: ParsedKey;
  subSequenceKeys?: ParsedKey[]; // For A+LC groups
  userInputs: Array<{key: string, isCorrect: boolean, isOptional?: boolean, position?: number}>;
  followingRCs: Array<{key: string, isCorrect: boolean, isOptional?: boolean}>;
  isMainKey: boolean;
  isSubSequenceGroup?: boolean; // True for A+LC groups
}

interface ALCAttempt {
  aInput?: {key: string, isCorrect: boolean};
  lcInput?: {key: string, isCorrect: boolean}; // Could be LC or cancelling input
  isComplete: boolean;
  isCancelled: boolean;
}

interface HighScore {
  comboName: string;
  time: number;
  accuracy: number;
  kpm: number;
  errors: number;
  date: string;
}

interface PracticeAreaProps {
  selectedCombo: ComboData | null;
  currentSeq: ParsedKey[];
  currentKeyIndex: number;
  kitingSettings: KitingSettings;
  clickFeedback: ClickFeedback;
  sequenceChunks: ParsedKey[][];
  currentChunkIndex: number;
  progress: SequenceProgress;
  keyStates: KeyState[];
  userInputKeys: UserInputKey[];
  practiceMode: boolean;
  showCompletion: boolean;
  stats: any;
  highScore: HighScore | null;
  pendingAutoAttack: boolean;
  onPracticeAreaClick: (event: React.MouseEvent) => void;
  onRetryClick: () => void;
  onStartPractice: () => void;
  renderKeyButton: (parsedKey: ParsedKey, isCompleted: boolean, isCurrent: boolean, isUpcoming: boolean) => React.ReactNode;
  getCompletionChunks: () => AlignedInput[][];
}

// Function to process A+LC inputs into proper pairs
const processALCInputs = (userInputs: Array<{key: string, isCorrect: boolean, isOptional?: boolean, position?: number}>): {
  alcAttempts: ALCAttempt[];
  followingRCs: Array<{key: string, isCorrect: boolean}>;
} => {
  console.log('=== PROCESSING A+LC INPUTS ===');
  console.log('Input data:', JSON.stringify(userInputs, null, 2));
  
  const alcAttempts: ALCAttempt[] = [];
  const followingRCs: Array<{key: string, isCorrect: boolean}> = [];
  
  // Group inputs into A+LC pairs based on position tracking
  let currentAttempt: ALCAttempt | null = null;
  let attemptCounter = 1;
  
  console.log('\nProcessing each input:');
  
  for (let i = 0; i < userInputs.length; i++) {
    const input = userInputs[i];
    console.log('\n--- Input ' + (i + 1) + ': "' + input.key + '" (position: ' + input.position + ', isCorrect: ' + input.isCorrect + ') ---');
    
    if (input.position === 0) {
      // A position input - start new attempt
      console.log('Position 0 detected - This should be an A input');
      
      if (currentAttempt) {
        console.log('Previous attempt was incomplete (A without LC/cancellation)');
        console.log('   Previous attempt:', JSON.stringify(currentAttempt, null, 2));
        currentAttempt.isCancelled = true;
        alcAttempts.push(currentAttempt);
        console.log('   Marked previous as cancelled and pushed to attempts');
      }
      
      console.log('Starting new attempt #' + attemptCounter);
      currentAttempt = { 
        aInput: { key: input.key, isCorrect: input.isCorrect },
        isComplete: false, 
        isCancelled: false 
      };
      
      // Check A input correctness logic
      if (input.key === 'A') {
        console.log('First position: A key is correct for position 0');
      } else {
        console.log('First position: "' + input.key + '" is incorrect for position 0 (should be A)');
      }
      
      console.log('   Current attempt after A input:', JSON.stringify(currentAttempt, null, 2));
      
    } else if (input.position === 1) {
      // LC position input (could be LC or cancelling input)
      console.log('Position 1 detected - This should be LC or cancelling input');
      
      if (currentAttempt) {
        console.log('   Adding to existing attempt...');
        currentAttempt.lcInput = { key: input.key, isCorrect: input.isCorrect };
        
        // Apply the A+LC logic rules:
        // A + LC = correct
        // A + RC = incorrect  
        // A + any other input = incorrect
        console.log('\nApplying A+LC logic rules:');
        console.log('   A input: "' + (currentAttempt.aInput?.key || 'undefined') + '" (correct: ' + (currentAttempt.aInput?.isCorrect || false) + ')');
        console.log('   Second input: "' + input.key + '" (correct: ' + input.isCorrect + ')');
        
        if (input.key === 'LC') {
          console.log('   Rule: A + LC = should be correct');
          if (currentAttempt.aInput?.isCorrect && input.isCorrect) {
            currentAttempt.isComplete = true;
            console.log('   Both A and LC are correct -> Attempt COMPLETE');
          } else {
            currentAttempt.isCancelled = true;
            console.log('   Either A or LC is incorrect -> Attempt CANCELLED');
            console.log('      A correct: ' + (currentAttempt.aInput?.isCorrect || false) + ', LC correct: ' + input.isCorrect);
          }
        } else if (input.key === 'RC') {
          console.log('   Rule: A + RC = incorrect (cancelling)');
          currentAttempt.isCancelled = true;
          console.log('   A + RC is always incorrect -> Attempt CANCELLED');
        } else {
          console.log('   Rule: A + "' + input.key + '" = incorrect (not LC)');
          currentAttempt.isCancelled = true;
          console.log('   A + non-LC input is incorrect -> Attempt CANCELLED');
        }
        
        console.log('   Final attempt state:', JSON.stringify(currentAttempt, null, 2));
        
        // Push completed/cancelled attempt
        alcAttempts.push(currentAttempt);
        console.log('   Pushed attempt #' + attemptCounter + ' to results');
        currentAttempt = null;
        attemptCounter++;
      } else {
        console.log('No current attempt to add LC input to!');
        // This might be a standalone RC - handle separately
        if (input.key === 'RC') {
          console.log('   This is a standalone RC (no A before it)');
          followingRCs.push({ key: input.key, isCorrect: input.isCorrect });
        }
      }
    } else {
      console.log('Unexpected position: ' + input.position + ' for input "' + input.key + '"');
      // Handle standalone RCs or other cases
      if (input.key === 'RC') {
        console.log('   Adding as standalone RC');
        followingRCs.push({ key: input.key, isCorrect: input.isCorrect });
      }
    }
  }
  
  // Handle any remaining incomplete attempt
  if (currentAttempt && currentAttempt.aInput && !currentAttempt.lcInput) {
    console.log('\nHandling incomplete attempt at end:');
    console.log('   Incomplete attempt:', JSON.stringify(currentAttempt, null, 2));
    currentAttempt.isCancelled = true;
    alcAttempts.push(currentAttempt);
    console.log('   Marked as cancelled and pushed to results');
  }
  
  console.log('\n=== FINAL RESULTS ===');
  console.log('ALC Attempts:', JSON.stringify(alcAttempts, null, 2));
  console.log('Following RCs:', JSON.stringify(followingRCs, null, 2));
  console.log('=== END PROCESSING ===\n');
  
  return { alcAttempts, followingRCs };
};

// Function to render A+LC attempts
const renderALCAttempts = (alcAttempts: ALCAttempt[]): React.ReactNode => {
  return alcAttempts.map((attempt, attemptIndex) => (
    <div key={attemptIndex} className="alc-attempt">
      <div className="alc-pair">
        {/* A position */}
        <div className="alc-position">
          {attempt.aInput ? (
            <div className={`user-key ${attempt.aInput.isCorrect ? 'correct' : 'incorrect'}`}>
              {attempt.aInput.key}
            </div>
          ) : (
            <div className="user-key missing">?</div>
          )}
        </div>
        
        {/* LC position */}
        <div className="alc-position">
          {attempt.lcInput ? (
            <div className={`user-key ${
              attempt.isComplete ? 'correct' : 'incorrect'
            } ${attempt.lcInput.key === 'RC' ? 'optional' : ''}`}>
              {attempt.lcInput.key}
            </div>
          ) : (
            <div className="user-key missing">?</div>
          )}
        </div>
      </div>
    </div>
  ));
};

const PracticeArea: React.FC<PracticeAreaProps> = memo(({
  selectedCombo,
  currentSeq,
  currentKeyIndex,
  kitingSettings,
  clickFeedback,
  sequenceChunks,
  currentChunkIndex,
  progress,
  keyStates,
  userInputKeys,
  practiceMode,
  showCompletion,
  stats,
  highScore,
  pendingAutoAttack,
  onPracticeAreaClick,
  onRetryClick,
  onStartPractice,
  renderKeyButton,
  getCompletionChunks
}) => {
  const currentKey = currentSeq[progress.currentKeyIndex];
  const isSequenceComplete = !currentKey;
  
  // Create intelligent input display with smart expected sequence matching
  const getIntelligentInputDisplay = () => {
    const displayItems: Array<{
      expectedKey: string | null;
      userInput: string;
      userInputClass: string;
    }> = [];
    
    // Get only non-RC expected keys for the expected sequence
    const expectedNonRCKeys = currentSeq.filter(key => key.keyType !== 'rightclick');
    
    let expectedIndex = 0;
    let userInputIndex = 0;
    
    // Process each user input and align with expected sequence
    while (userInputIndex < userInputKeys.length) {
      const userInput = userInputKeys[userInputIndex];
      
      if (userInput.isOptional) {
        // Right-click: Show in blue with empty expected key above
        displayItems.push({
          expectedKey: null, // Empty space above RC
          userInput: userInput.key,
          userInputClass: 'optional'
        });
        userInputIndex++;
        continue;
      }
      
      // Non-optional input: Check if it matches current expected key
      const currentExpected = expectedNonRCKeys[expectedIndex];
      
      if (userInput.isCorrect && currentExpected) {
        // Correct input: Show expected key above and advance expected sequence
        displayItems.push({
          expectedKey: currentExpected.displayKey,
          userInput: userInput.key,
          userInputClass: 'correct'
        });
        expectedIndex++; // Advance expected sequence
      } else {
        // Incorrect input: Show current expected key above but don't advance expected sequence
        displayItems.push({
          expectedKey: currentExpected?.displayKey || '?',
          userInput: userInput.key,
          userInputClass: 'incorrect'
        });
        // Don't advance expectedIndex - keep expecting the same key
      }
      
      userInputIndex++;
    }
    
    return displayItems;
  };
  
  // Show completion screen if practice is complete
  console.log('🖥️ PracticeArea render check:', { showCompletion, practiceMode });
  
  if (showCompletion) {
    console.log('✅ Rendering completion screen');
    return (
      <div className="practice-area completion-mode">
        <div className="completion-display">
          <h3>Practice Complete!</h3>
          
          {/* Full sequence with intelligent user input alignment */}
          <div className="completion-overview">
            <h4>Complete Sequence Review</h4>
            <div className="completion-sequence-chunked">
              {getCompletionChunks().map((chunk, chunkIndex) => (
                <div key={chunkIndex} className="completion-chunk">
                  <div className="completion-chunk-keys">
                    {chunk.map((alignedInput, keyIndex) => (
                      <React.Fragment key={keyIndex}>
                        <div className={`completion-key-group main-key-group ${alignedInput.isSubSequenceGroup ? 'sub-sequence-group' : ''}`}>
                          {/* Expected key(s) at top */}
                          <div className="expected-key">
                            {alignedInput.isSubSequenceGroup && alignedInput.subSequenceKeys ? (
                              // Render A+LC group horizontally without arrows
                              <div className="sub-sequence-keys">
                                {alignedInput.subSequenceKeys.map((subKey, subIndex) => (
                                  <div key={subIndex} className="sub-sequence-key">
                                    {renderKeyButton(subKey, false, false, false)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              // Single key
                              renderKeyButton(alignedInput.expected, false, false, false)
                            )}
                          </div>
                          
                          {/* User inputs below - all attempts for this expected key */}
                          <div className="user-inputs-section">
                            {alignedInput.isSubSequenceGroup && alignedInput.subSequenceKeys ? (
                              // A+LC sub-sequence: use proper pairing logic
                              (() => {
                                const { alcAttempts } = processALCInputs(alignedInput.userInputs);
                                
                                return (
                                  <div className="alc-sequence-inputs">
                                    {/* Render A+LC attempts */}
                                    <div className="alc-attempts">
                                      {renderALCAttempts(alcAttempts)}
                                    </div>
                                    
                                    {/* Render actual following RCs at bottom if any */}
                                    {alignedInput.followingRCs.length > 0 && (
                                      <div className="following-rcs-bottom">
                                        {alignedInput.followingRCs.map((rcInput, rcIndex) => (
                                          <div key={rcIndex} className="user-key optional">
                                            {rcInput.key}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            ) : (
                              // Single key: render inputs normally
                              <div className="user-inputs-stack main-inputs">
                                {alignedInput.userInputs.length > 0 ? (
                                  alignedInput.userInputs.map((userInput, inputIndex) => (
                                    <div key={inputIndex} className={`user-key ${
                                      userInput.isCorrect ? 'correct' : 'incorrect'
                                    }`}>
                                      {userInput.key}
                                    </div>
                                  ))
                                ) : (
                                  <div className="user-key missing">?</div>
                                )}
                              </div>
                            )}
                            
                            {/* RCs that follow this key (positioned to the right/after) */}
                            {alignedInput.followingRCs.length > 0 && (
                              <div className="following-rcs">
                                {alignedInput.followingRCs.map((rcInput, rcIndex) => (
                                  <div key={rcIndex} className="user-key optional rc-after">
                                    {rcInput.key}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        {keyIndex < chunk.length - 1 && (
                          <div className="key-arrow">→</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="completion-stats">
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Time:</span>
                  <span className="stat-value">{stats.totalTime}s</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Keys per Minute:</span>
                  <span className="stat-value">{stats.kpm}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Accuracy:</span>
                  <span className="stat-value">{stats.accuracy}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Errors:</span>
                  <span className="stat-value">{userInputKeys.filter(key => !key.isCorrect && !key.isOptional).length}</span>
                </div>
              </div>
            </div>
          )}

          <div className="completion-controls">
            <button className="retry-button" onClick={onRetryClick}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`practice-area ${practiceMode && kitingSettings.enabled ? 'kiting-enabled' : ''} ${!practiceMode ? 'preview-mode' : ''}`}
      onMouseDown={practiceMode ? onPracticeAreaClick : undefined}
      onContextMenu={(e) => practiceMode && kitingSettings.enabled && e.preventDefault()}
    >
      
        
      {/* Click feedback at mouse position - only in practice mode */}
      {practiceMode && clickFeedback.show && (
        <div 
          className="click-feedback"
          style={{
            left: `${clickFeedback.x}px`,
            top: `${clickFeedback.y}px`
          }}
        >
          <div className="click-ripple"></div>
        </div>
      )}

 

      
      <div className="practice-info">     
      </div>
 
      

      {/* Complete sequence display */}
      <div className="complete-sequence-display">
        <div className="sequence-progress-info">
          Progress: {progress.completedKeys}/{progress.totalKeys} | Errors: {progress.errorCount}
        </div>
        <div className="sequence-display">
          {sequenceChunks.map((chunk, chunkIndex) => (
            <div key={chunkIndex} className={`sequence-chunk ${chunkIndex === progress.currentChunkIndex ? 'active-chunk' : ''}`}>
              <div className="chunk-keys">
                {chunk.map((parsedKey, keyIndex) => {
                  // Calculate global key index dynamically
                  let globalKeyIndex = 0;
                  for (let i = 0; i < chunkIndex; i++) {
                    globalKeyIndex += sequenceChunks[i].length;
                  }
                  globalKeyIndex += keyIndex;
                  const keyState = keyStates[globalKeyIndex];
                  const isCompleted = keyState?.status === 'completed';
                  const isCurrent = keyState?.status === 'current';
                  const isUpcoming = keyState?.status === 'upcoming';
                  
                  // Check if next key is in different sub-sequence (for arrow logic)
                  const nextKey = chunk[keyIndex + 1];
                  const showArrow = keyIndex < chunk.length - 1 && 
                                   nextKey && 
                                   (parsedKey.subSequenceId !== nextKey.subSequenceId || 
                                    parsedKey.keyType === 'rightclick' || 
                                    nextKey.keyType === 'rightclick');
                  
                  // Check if next key is in same sub-sequence (for spacing logic)
                  const isNextInSameSubSequence = nextKey && 
                                                 parsedKey.subSequenceId === nextKey.subSequenceId &&
                                                 parsedKey.subSequenceId !== undefined &&
                                                 nextKey.subSequenceId !== undefined;
                  
                  return (
                    <React.Fragment key={keyIndex}>
                      <div className={`combo-key-display ${keyState?.status || ''} ${keyState?.userInput && !keyState.userInput.isCorrect && !keyState.userInput.isOptional ? 'error' : ''} ${isNextInSameSubSequence ? 'tight-spacing' : ''}`}>
                        {renderKeyButton(parsedKey, isCompleted, isCurrent, isUpcoming)}
                      </div>
                      {showArrow && (
                        <div className="key-arrow">→</div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider line between complete sequence and active practice */}
      <div className="sequence-divider"></div>

      {/* Active sequence section to practice */}
      <div className="active-sequence-section">
        <div className="sequence-line">
          {sequenceChunks[progress.currentChunkIndex]?.map((parsedKey, keyIndex) => {
            // Calculate global key index dynamically for active sequence
            let globalKeyIndex = 0;
            for (let i = 0; i < progress.currentChunkIndex; i++) {
              globalKeyIndex += sequenceChunks[i].length;
            }
            globalKeyIndex += keyIndex;
            const keyState = keyStates[globalKeyIndex];
            const isCompleted = keyState?.status === 'completed';
            const isCurrent = keyState?.status === 'current';
            const isUpcoming = keyState?.status === 'upcoming';
            
            // Check if next key is in different sub-sequence (for arrow logic)
            const nextKey = sequenceChunks[progress.currentChunkIndex]?.[keyIndex + 1];
            const showArrow = keyIndex < sequenceChunks[progress.currentChunkIndex].length - 1 && 
                             nextKey && 
                             (parsedKey.subSequenceId !== nextKey.subSequenceId || 
                              parsedKey.keyType === 'rightclick' || 
                              nextKey.keyType === 'rightclick');
            
            return (
              <React.Fragment key={keyIndex}>
                <div className={`sequence-key-container ${keyState?.status || ''} ${keyState?.userInput && !keyState.userInput.isCorrect && !keyState.userInput.isOptional ? 'error' : ''}`}>
                  {renderKeyButton(parsedKey, isCompleted, isCurrent, isUpcoming)}
                </div>
                {showArrow && (
                  <div className="key-arrow">→</div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>



      {practiceMode && (
        <div className="user-input-display">
{/*           <h4>Your Input vs Expected:</h4>
 */}          <div className="input-comparison-line">
            {getIntelligentInputDisplay().map((displayItem, index) => (
              <div key={index} className="input-key-pair">
                <div className="expected-key-mini">
                  {displayItem.expectedKey || ''}
                </div>
                <div className={`user-key-mini ${displayItem.userInputClass}`}>
                  {displayItem.userInput}
                </div>
              </div>
            ))}
            {progress.currentKeyIndex < progress.totalKeys && (
              <div className="input-cursor">|</div>
            )}
          </div>
        </div>
      )}

   
      
      {/* User input display - shows intelligent sequence matching - only in practice mode */}
 

      {/* Practice Controls */}
      {!practiceMode ? (
        <div className="practice-controls">
          {/* High Score Display */}
          {highScore && (
            <div className="high-score-display">
              <h4>🏆 Best Score</h4>
              <div className="high-score-stats">
                <span className="high-score-stat">
                  <strong>{highScore.accuracy.toFixed(1)}%</strong> accuracy
                </span>
                <span className="high-score-stat">
                  <strong>{highScore.time.toFixed(1)}s</strong> time
                </span>
                <span className="high-score-stat">
                  <strong>{highScore.kpm}</strong> KPM
                </span>
              </div>
            </div>
          )}
          
          <div className="practice-start-section">
            <button className="start-button" onClick={onStartPractice}>
              🎯 Start
            </button>
          </div>
        </div>
      ) : (
        <><div className="practice-active-controls">
            <button className="reset-button" onClick={onRetryClick}>
              🔄 Reset Practice
            </button>
          </div><div className="progress-info">
              Progress: {progress.completedKeys}/{progress.totalKeys} |
              Errors: {progress.errorCount}
            </div></>
      )}
      
    </div>
    
  );
});

export default PracticeArea;